/**
 * Starter-pack install wizard (SA-84 Flow A, spec §5.1): pick a pack from
 * the catalog, walk each of its present workflows through a conflict-aware
 * preview (with per-entry "use pack version" resolution), apply & publish,
 * then show a per-workflow summary. Task 6's hooks
 * (`useWorkflowPacks`/`usePackPreview`/`usePackApply`) do the fetch/mutate
 * work — this component owns only the pick/preview/done state machine.
 *
 * `usePackPreview`/`usePackApply` take the workflow id at hook-call time,
 * so they can't be called in a loop over the queue — both are called once,
 * at the top level, for the CURRENT workflow (`queue[index] ?? ''`).
 * Advancing `index` re-renders with the next id, same as any other
 * per-id-mutation call site in this codebase.
 *
 * Preview fires immediately on entry to each workflow; taken-set toggles
 * are debounced (300ms) with the previous preview kept rendered and dimmed
 * until the refresh lands (SA-127).
 */
import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Switch,
} from '@/components/ui'
import { useWorkflowPacks, usePackApply, usePackPreview } from '@/hooks/useWorkflowPacks'
import { extractPublishFindings } from '../configure/publishFindings'
import { writeConflict } from '@/utils/writeConflict'
import { cn } from '@/utils'
import type { PackPreviewResponse, PackSummary } from '@/types/workflowConfig'

type Stage = 'pick' | 'preview' | 'done'
type ResultStatus = 'published' | 'no_op' | 'skipped' | 'failed'
interface ResultEntry {
  status: ResultStatus
  detail?: string
  versionNumber?: number
}

const RESULT_LABEL: Record<ResultStatus, string> = {
  published: 'Published',
  no_op: 'No changes needed',
  skipped: 'Skipped',
  failed: 'Failed',
}

function resultLabel(entry: ResultEntry): string {
  if (entry.status === 'published' && entry.versionNumber != null) {
    return `Published v${entry.versionNumber}`
  }
  return RESULT_LABEL[entry.status]
}

/** Best-effort message for a failed preview mutation (network error, 400,
 * an archived/removed workflow mid-wizard) — validation-shaped errors get
 * their findings spelled out, everything else gets a generic retry prompt. */
function describePreviewError(error: unknown): string {
  const findings = extractPublishFindings(error)
  if (findings && findings.length > 0) {
    return findings.map((f) => `${f.path}: ${f.message}`).join('; ')
  }
  return 'Could not check this workflow — try again.'
}

function localized(blob: Record<string, string>): string {
  return blob.en ?? Object.values(blob)[0] ?? ''
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string') return v
  return JSON.stringify(v)
}

export function PackInstallDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const packsQuery = useWorkflowPacks(open)
  const [stage, setStage] = useState<Stage>('pick')
  const [packId, setPackId] = useState<string | null>(null)
  const [queue, setQueue] = useState<string[]>([])
  const [index, setIndex] = useState(0)
  const [taken, setTaken] = useState<Record<string, Set<string>>>({})
  const [results, setResults] = useState<Record<string, ResultEntry>>({})
  // The response is stored WITH the issuedFor key it answers, so staleness
  // is derivable: previous content stays rendered (dimmed) during a
  // toggle refresh instead of blanking, and Apply gates on freshness.
  const [previewData, setPreviewData] = useState<{
    key: string
    data: PackPreviewResponse
  } | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  // SA-416: the preview's basis moved before Apply — say so, show a fresh one.
  const [basisMoved, setBasisMoved] = useState(false)

  const currentWorkflowId = queue[index] ?? ''
  const preview = usePackPreview(currentWorkflowId)
  const apply = usePackApply(currentWorkflowId)

  const currentTaken = taken[currentWorkflowId] ?? new Set<string>()
  const takenKey = [...currentTaken].sort().join(',')
  const currentPreviewKey = `${currentWorkflowId}::${takenKey}`
  const previewStale = previewData !== null && previewData.key !== currentPreviewKey

  // Stale-response guard, same shape as ConfigureBar.tsx:25-42's ref.
  const issuedForRef = useRef('')

  // Guards a late apply response (Escape/backdrop closed the dialog — and
  // reset its state — while the mutation was still in flight) from writing
  // into a wizard that's no longer showing this workflow.
  const openRef = useRef(open)
  openRef.current = open

  // Reset the whole wizard whenever the dialog closes, so the next open
  // starts clean at 'pick' rather than resuming a stale mid-flow state.
  useEffect(() => {
    if (open) return
    setStage('pick')
    setPackId(null)
    setQueue([])
    setIndex(0)
    setTaken({})
    setResults({})
    setPreviewData(null)
    setPreviewError(null)
    setBasisMoved(false)
  }, [open])

  // Shared by the debounced auto-fire below and the manual Retry button —
  // same stale-response guard (issuedForRef) on both success AND error, so
  // a slow response from an abandoned attempt (workflow advanced, or the
  // taken-set changed again before it landed) never clobbers newer state.
  function firePreview(issuedFor: string, takePack: string[]) {
    preview.mutate(
      { pack: packId!, take_pack: takePack },
      {
        onSuccess: (result) => {
          if (issuedForRef.current === issuedFor) setPreviewData({ key: issuedFor, data: result })
        },
        onError: (error) => {
          if (issuedForRef.current === issuedFor) setPreviewError(describePreviewError(error))
        },
      },
    )
  }

  // Fires on entry to a workflow and on every taken-set toggle. Entry is
  // immediate — there is nothing to coalesce, and every path into a
  // workflow (Continue, advance, dialog-close reset) nulls previewData
  // first. Toggles debounce 300ms; unlike the pre-SA-127 shape the data
  // is NOT nulled here, so the previous preview stays rendered (dimmed
  // via previewStale) and canApply gates on freshness instead.
  useEffect(() => {
    if (stage !== 'preview' || !packId || !currentWorkflowId) return
    setPreviewError(null)
    const issuedFor = `${currentWorkflowId}::${takenKey}`
    issuedForRef.current = issuedFor
    if (previewData !== null && previewData.key === issuedFor) return // toggled back to what we have
    const takePack = takenKey ? takenKey.split(',') : []
    if (previewData === null) {
      firePreview(issuedFor, takePack)
      return
    }
    const timer = setTimeout(() => firePreview(issuedFor, takePack), 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, packId, currentWorkflowId, takenKey])

  function handleRetryPreview() {
    if (!packId || !currentWorkflowId) return
    setPreviewError(null)
    const issuedFor = `${currentWorkflowId}::${takenKey}`
    issuedForRef.current = issuedFor
    const takePack = takenKey ? takenKey.split(',') : []
    firePreview(issuedFor, takePack)
  }

  const selectedPack: PackSummary | null =
    packsQuery.data?.find((p) => p.pack === packId) ?? null
  const presentIds = selectedPack?.workflows.filter((w) => w.present).map((w) => w.workflow_id) ?? []

  function handleContinue() {
    if (!selectedPack) return
    setQueue(presentIds)
    setIndex(0)
    setTaken({})
    setResults({})
    setPreviewData(null)
    setPreviewError(null)
    setBasisMoved(false)
    setStage(presentIds.length > 0 ? 'preview' : 'done')
  }

  function advance(status: ResultStatus, detail?: string, versionNumber?: number) {
    setResults((r) => ({ ...r, [currentWorkflowId]: { status, detail, versionNumber } }))
    setPreviewData(null)
    setPreviewError(null)
    setBasisMoved(false)
    const next = index + 1
    if (next >= queue.length) {
      setStage('done')
    } else {
      setIndex(next)
    }
  }

  function toggleTaken(ref: string, use: boolean) {
    setBasisMoved(false)
    setTaken((t) => {
      const next = new Set(t[currentWorkflowId] ?? [])
      if (use) next.add(ref)
      else next.delete(ref)
      return { ...t, [currentWorkflowId]: next }
    })
  }

  function handleApply() {
    if (!packId || !currentWorkflowId || !previewData) return
    setBasisMoved(false)
    apply.mutate(
      {
        pack: packId,
        take_pack: [...currentTaken],
        // What the preview on screen was woven over.
        basis: {
          expectedUpdatedAt: previewData.data.draft_updated_at ?? null,
          baseVersionNumber: previewData.data.base_version_number,
          versionNumber: previewData.data.current_version_number ?? null,
        },
      },
      {
        onSuccess: (result) => {
          if (!openRef.current) return
          advance(
            result.no_op ? 'no_op' : 'published',
            undefined,
            result.version?.version_number,
          )
        },
        onError: (error) => {
          if (!openRef.current) return
          if (writeConflict(error)) {
            // Someone saved a draft, the template moved, or a version was
            // published since this preview: nothing was written. Show what
            // Apply would do NOW.
            setBasisMoved(true)
            setPreviewData(null)
            handleRetryPreview()
            return
          }
          const findings = extractPublishFindings(error)
          const detail = findings
            ? findings.map((f) => `${f.path}: ${f.message}`).join('; ')
            : 'Apply failed — try again.'
          setResults((r) => ({ ...r, [currentWorkflowId]: { status: 'failed', detail } }))
        },
      },
    )
  }

  function handleSkip() {
    advance('skipped')
  }

  const currentResult = results[currentWorkflowId]
  const hasFailed = currentResult?.status === 'failed'
  const conflicts = previewData?.data.entries.filter((e) => e.bucket === 'conflict') ?? []
  const adds = previewData?.data.entries.filter((e) => e.bucket === 'adds') ?? []
  const noChange = previewData?.data.entries.filter((e) => e.bucket === 'no_change') ?? []
  const findings = previewData?.data.findings ?? []
  const canApply = !!previewData && !previewStale && findings.length === 0 && !apply.isPending

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      {/* size="lg" deliberately deviates from sibling dialogs' size="sm" —
          this one renders conflict groups plus per-leaf mine-vs-pack diffs,
          which need more width than a plain confirm/form dialog. */}
      <ModalContent size="lg" showClose={false}>
        <ModalHeader>
          <ModalTitle>
            {stage === 'pick' && 'Install a starter pack'}
            {stage === 'preview' &&
              `Workflow ${index + 1} of ${queue.length}: ${currentWorkflowId}`}
            {stage === 'done' && 'Starter pack installed'}
          </ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-3">
          {stage === 'pick' && (
            <>
              {packsQuery.isLoading && (
                <p className="text-sm text-muted-foreground">Loading starter packs…</p>
              )}
              {packsQuery.isError && (
                <p className="text-sm text-destructive">Couldn't load starter packs.</p>
              )}
              <fieldset className="space-y-3">
                <legend className="sr-only">Starter pack</legend>
                {(packsQuery.data ?? []).map((pack) => (
                  <div
                    key={pack.pack}
                    className="rounded-lg border border-border p-3 space-y-2 has-checked:border-primary"
                  >
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="starter-pack"
                        value={pack.pack}
                        checked={packId === pack.pack}
                        onChange={() => setPackId(pack.pack)}
                        className="mt-1"
                      />
                      <span>
                        <p className="text-sm font-medium text-foreground">{localized(pack.name)}</p>
                        <p className="text-xs text-muted-foreground">{localized(pack.description)}</p>
                      </span>
                    </label>
                    <ul className="pl-6 space-y-1">
                      {pack.workflows.map((w) => (
                        <li
                          key={w.workflow_id}
                          className={cn('flex items-center gap-2 text-xs', !w.present && 'opacity-60')}
                        >
                          <span className="font-mono text-muted-foreground">{w.workflow_id}</span>
                          {!w.present && (
                            <Badge variant="outline">Not installed on this deployment</Badge>
                          )}
                          {w.present && w.conflict && <Badge variant="destructive">Needs attention</Badge>}
                          {w.present && w.configured && !w.conflict && (
                            <Badge className="bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-800">
                              Customized
                            </Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </fieldset>
              {selectedPack && presentIds.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  None of this pack's workflows are installed on this deployment.
                </p>
              )}
            </>
          )}

          {stage === 'preview' && (
            <>
              {basisMoved && (
                <Alert variant="warning">
                  <AlertDescription>
                    This workflow's configuration changed since this preview, so nothing was applied.
                    Review the refreshed preview and apply again.
                  </AlertDescription>
                </Alert>
              )}
              {previewData?.data.draft_differs && (
                <Alert variant="warning">
                  <AlertDescription>
                    This workflow has unsaved customization edits; they are included in what gets
                    published.
                  </AlertDescription>
                </Alert>
              )}
              {findings.length > 0 && (
                <ul className="text-xs text-destructive space-y-0.5">
                  {findings.map((f, i) => (
                    <li key={i}>
                      <span className="font-mono">{f.path}</span>: {f.message}
                    </li>
                  ))}
                </ul>
              )}
              {hasFailed && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                  {currentResult?.detail}
                </div>
              )}
              {previewError && !hasFailed && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                  {previewError}
                </div>
              )}
              {!previewData && !hasFailed && !previewError && (
                <p className="text-sm text-muted-foreground">Checking this workflow…</p>
              )}
              {previewData && (
                <div
                  className={cn('space-y-3', previewStale && 'opacity-60')}
                  aria-busy={previewStale || undefined}
                >
                  {previewStale && (
                    <p className="text-xs text-muted-foreground">Updating preview…</p>
                  )}
                  {conflicts.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-foreground">Conflicts</p>
                      <ul className="space-y-2">
                        {conflicts.map((entry) => (
                          <li key={entry.ref} className="rounded-lg border border-border p-2 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-xs">{entry.ref}</span>
                              <label className="flex items-center gap-1.5 text-xs text-foreground">
                                <Switch
                                  size="sm"
                                  aria-label={`use pack version for ${entry.ref}`}
                                  checked={currentTaken.has(entry.ref)}
                                  onCheckedChange={(checked) => toggleTaken(entry.ref, checked)}
                                />
                                Use pack version
                              </label>
                            </div>
                            <ul className="text-xs text-muted-foreground space-y-0.5">
                              {entry.detail.map((leaf, i) => (
                                <li key={i}>
                                  {leaf.path && <span className="font-mono">{leaf.path}: </span>}
                                  mine: {formatValue(leaf.mine)} — pack: {formatValue(leaf.pack)}
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {adds.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-foreground">New from pack</p>
                      <ul className="text-xs text-muted-foreground space-y-0.5">
                        {adds.map((e) => (
                          <li key={e.ref} className="font-mono">
                            {e.ref}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {noChange.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-foreground">No change</p>
                      <ul className="text-xs text-muted-foreground space-y-0.5">
                        {noChange.map((e) => (
                          <li key={e.ref} className="font-mono">
                            {e.ref}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {stage === 'done' && (
            <ul className="text-sm space-y-1">
              {queue.map((id) => (
                <li key={id} className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs">{id}</span>
                  <span>{results[id] ? resultLabel(results[id]) : '—'}</span>
                </li>
              ))}
              {queue.length === 0 && (
                <li className="text-muted-foreground">
                  None of this pack's workflows are installed on this deployment.
                </li>
              )}
            </ul>
          )}
        </ModalBody>
        <ModalFooter>
          {stage === 'pick' && (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!selectedPack || presentIds.length === 0} onClick={handleContinue}>
                Continue
              </Button>
            </>
          )}
          {stage === 'preview' && (
            <>
              {/* Only exit besides Escape/backdrop while mid-wizard — a failed
                  preview otherwise dead-ends here with nothing else clickable. */}
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {/* Disabled while a mutation is in flight — skipping mid-apply would
                  advance the queue and then have the eventual (stale) onSuccess/
                  onError clobber the 'skipped' result once it lands. */}
              <Button variant="outline" disabled={apply.isPending} onClick={handleSkip}>
                Skip
              </Button>
              {previewError ? (
                <Button
                  disabled={preview.isPending}
                  isLoading={preview.isPending}
                  onClick={handleRetryPreview}
                >
                  {preview.isPending ? 'Retrying…' : 'Retry'}
                </Button>
              ) : hasFailed ? (
                <Button disabled={apply.isPending} isLoading={apply.isPending} onClick={handleApply}>
                  {apply.isPending ? 'Retrying…' : 'Retry'}
                </Button>
              ) : (
                <Button disabled={!canApply} isLoading={apply.isPending} onClick={handleApply}>
                  {apply.isPending ? 'Applying…' : 'Apply & publish'}
                </Button>
              )}
            </>
          )}
          {stage === 'done' && <Button onClick={onClose}>Close</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
